import { Trans, useLingui } from "@lingui/react/macro";
import { FileDown } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { AppModel } from "../../../app/app-model";
import { ErrorPage } from "../../../app/error-page";
import { CertificationDisclaimer } from "../../../app/footer";
import { DocumentTitle } from "../../../app/layout/document-title";
import { PasswordPrompt } from "../../../app/password-prompt";
import logoPng from "../../../assets/sq_logo.png";
import { AgeSexBadge, ageSexOf } from "../../../components/age-sex-badge";
import { Button } from "../../../components/ui-kit/button";
import { Spinner } from "../../../components/ui-kit/spinner";
import { getServerTodayISO } from "../../../utils/server-time";
import { assert } from "../../../utils/utils";
import { PatientProfileChartTabs } from "./patient-profile-chart-tabs";

/**
 * The profile's Trends charts alone, without the app header or sidebar — opened
 * in a new tab from the chart tabs' "Open in new tab" button. Lives outside
 * MainLayout, so it does that layout's login step itself.
 */
export const TrendsWindowRoute = observer(function TrendsWindowRoute(props: {
  app: AppModel;
}) {
  const { t } = useLingui();
  const { id } = useParams<{ id: string }>();
  assert(id !== undefined, "Patient ID param not found");
  const patientId = parseInt(id);
  const [searchParams] = useSearchParams();
  const api = props.app.seerlinqApi;
  const [printing, setPrinting] = useState(false);

  // Always light, whatever the app's theme: the window is what gets printed
  // and shared, and the PDF must come out the same for everyone. A layout
  // effect, so not even the first frame paints dark. Nothing in the window can
  // toggle the theme back (the footer switch lives in MainLayout), and leaving
  // the window hands the app its own theme again.
  useLayoutEffect(() => {
    document.documentElement.classList.remove("dark");
    return () => props.app.setTheme();
  }, [props.app]);

  const patient = useMemo(
    () => props.app.initPatient(patientId),
    [patientId, props.app],
  );

  // Patient.init reads the permissions, so it has to wait for the login.
  useEffect(() => {
    void (async () => {
      await api.assumeLoggedIn();
      await patient.init();
    })();
  }, [api, patient]);

  // Lays the page out at paper width before handing it to the browser's print
  // dialog: the charts only redraw on a resize, which printing alone doesn't
  // reliably give them in time. Put back on `afterprint`, not when `print()`
  // returns: mobile browsers return before the dialog has read the page.
  const today = getServerTodayISO();
  const printTitle = `${patient.data?.full_name ?? patientId} – ${t`Trends`} – ${today}`;
  const exportPdf = async () => {
    if (printing) return;
    const title = document.title;
    setPrinting(true);
    await chartsRedrawn();
    window.addEventListener(
      "afterprint",
      () => {
        document.title = title;
        setPrinting(false);
      },
      { once: true },
    );
    // The browser's default PDF filename.
    document.title = printTitle;
    window.print();
  };

  // The browser's own Cmd/Ctrl+P would print the screen layout — ruler,
  // CURRENT row, screen-width charts — so it goes through the export too.
  // No deps: re-bound each render, so the handler never sees a stale export.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key !== "p" || !patient.loaded) {
        return;
      }
      e.preventDefault();
      void exportPdf();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  });

  // MainLayout's re-auth prompt: without it a 428 would wait forever.
  const prompt = api.promptPassword && <PasswordPrompt api={api} />;

  if (patient.error) {
    return (
      <ErrorPage
        title={t`Error loading patient`}
        message={patient.error.message}
      />
    );
  }

  if (!api.loggedIn || !patient.loaded) {
    return (
      <div className="grow flex items-center justify-center">
        {prompt}
        <Spinner />
      </div>
    );
  }

  // A bounded scroll container, so the charts' `scopedScroll` mode pins the
  // events bar to its top and the zoom bar to its bottom. In print it grows to
  // its full height instead, or only the first screenful would make the page.
  return (
    <div
      className="h-screen overflow-auto px-4 print:h-auto print:overflow-visible print:px-0"
      style={
        printing
          ? { width: PRINT_WIDTH, margin: "0 auto", padding: 0 }
          : undefined
      }
    >
      <style>
        {printCss(printTitle, `Copyright © ${today.slice(0, 4)} Seerlinq`)}
      </style>
      {prompt}
      <DocumentTitle app={props.app} />
      <img src={logoPng} alt="Seerlinq" className="mx-auto mt-6 w-[320px]" />
      {patient.data && (
        <div className="my-5 flex items-center justify-center gap-3 text-2xl font-semibold">
          {patient.data.full_name}
          <AgeSexBadge
            {...ageSexOf(patient.data)}
            dateOfBirth={patient.data.date_of_birth}
            className="px-3 py-1 text-base"
          />
        </div>
      )}
      <PatientProfileChartTabs
        patient={patient}
        api={api}
        standalone
        initialTab={searchParams.get("tab") ?? undefined}
        printing={printing}
        jumpBarEnd={
          <Button
            size="sm"
            variant="primary"
            icon={FileDown}
            disabled={printing}
            onClick={exportPdf}
          >
            <Trans>Export as PDF</Trans>
          </Button>
        }
      />
      <CertificationDisclaimer className="mx-auto my-6 max-w-xl text-center text-xs" />
    </div>
  );
});

// The width the page is laid out at for print. A4 portrait less the @page
// margins below is 190mm, 718 CSS px — but Chrome's print dialog lays a page
// out at 4/3 of that and scales it down to fit, so a 718px layout fills only
// three quarters of the sheet. Laid out at the 4/3 width, it fills Chrome's
// sheet exactly; a browser that lays out at the plain paper width (and
// headless PDF rendering) sees the page overflow and shrinks it to fit, which
// fills the sheet just the same.
const PRINT_WIDTH = Math.round((718 * 4) / 3);

// Without color-adjust the browser drops backgrounds — the threshold bands,
// the age+sex pill — to save ink. Any margin box of our own replaces all of
// Chrome's print header and footer — its timestamp, the page URL — so the page
// numbers are restated here. JSON.stringify quotes and escapes like CSS does.
function printCss(header: string, footer: string) {
  return `
@page {
  size: A4 portrait;
  margin: 10mm;
  font-size: 8pt;
  color: var(--muted-foreground);
  @top-center { content: ${JSON.stringify(header)}; }
  @bottom-left { content: ${JSON.stringify(footer)}; }
  @bottom-right { content: counter(page) "/" counter(pages); }
}
@media print {
  body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
}
`;
}

// Resolves once every chart has been redrawn at its new width, and stayed so
// for two frames. The redraw is a cascade — the width change reaches each chart
// through a ResizeObserver, and ECharts repaints on its own next frame — whose
// length grows with the data, so a fixed number of frames can print charts
// still drawn at their old width. Capped, so a chart that never settles can't
// hold the export up.
async function chartsRedrawn(): Promise<void> {
  const deadline = performance.now() + 3000;
  let settledFrames = 0;
  while (settledFrames < 2 && performance.now() < deadline) {
    await new Promise((resolve) => requestAnimationFrame(resolve));
    const settled = [
      ...document.querySelectorAll<HTMLElement>("[_echarts_instance_]"),
    ].every((chart) => {
      const svg = chart.querySelector("svg");
      return !svg || Math.abs(svg.width.baseVal.value - chart.clientWidth) < 1;
    });
    settledFrames = settled ? settledFrames + 1 : 0;
  }
}
